import * as React from "react"
import { Link, useParams } from "react-router-dom"

import { AlfredBulkImportStep } from "@/components/alfred/bulk-import/alfred-bulk-import-step"
import { AlfredBulkPreviewStep } from "@/components/alfred/bulk-import/alfred-bulk-preview-step"
import { AlfredBulkUploadStep } from "@/components/alfred/bulk-import/alfred-bulk-upload-step"
import { useAlfredBulkImportJobContext } from "@/components/alfred/bulk-import/alfred-bulk-import-job-context"
import { DashboardHeader } from "@/components/shared/dashboard-header"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Stepper, type Step } from "@/components/ui/stepper"
import { useAlfredClients } from "@/hooks/use-alfred-clients"
import { toast } from "@/hooks/use-toast"
import { alfredDeviceService } from "@/services/alfred-device-service"
import type {
  AlfredBulkImportSelection,
  AlfredBulkImportUploadResult,
} from "@/types/alfred-bulk-import"

const BULK_IMPORT_STEPS: Step[] = [
  { id: "upload", title: "Upload" },
  { id: "preview", title: "Preview" },
  { id: "import", title: "Import" },
]

type BulkImportStep = "upload" | "preview" | "import"

export function AlfredBulkAddPage() {
  const { id } = useParams()
  const { startJob } = useAlfredBulkImportJobContext()
  const [currentStep, setCurrentStep] = React.useState<BulkImportStep>("upload")
  const [selection, setSelection] = React.useState<AlfredBulkImportSelection | null>(null)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [uploadResult, setUploadResult] = React.useState<AlfredBulkImportUploadResult | null>(null)
  const [uploadError, setUploadError] = React.useState<string | null>(null)
  const { data: clients, error: clientsError } = useAlfredClients()
  const numericClientId = id ? Number(id) : undefined
  const isClientScoped = numericClientId !== undefined
  const scopedClient = numericClientId
    ? clients?.find((client) => client.id === numericClientId)
    : undefined
  const pageTitle = scopedClient
    ? `Bulk add devices to ${scopedClient.clientId}`
    : "Bulk add devices"
  const validClientIds = React.useMemo(
    () => new Set(clients?.map((client) => client.clientId) ?? []),
    [clients],
  )

  const submitImport = async () => {
    if (!selection) return

    setIsSubmitting(true)
    setUploadResult(null)
    setUploadError(null)

    try {
      const result = await alfredDeviceService.bulkCreate(selection.file)
      startJob(result)
      setUploadResult(result)
      toast({
        title: "Bulk import started",
        description: `Job ${result.jobId} queued ${result.totalRows} row${result.totalRows === 1 ? "" : "s"} for processing.`,
        variant: "success",
      })
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : "Unable to upload the CSV file")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleConfirmImport = () => {
    setCurrentStep("import")
    void submitImport()
  }

  return (
    <>
      <DashboardHeader />
      <main className="container mx-auto flex flex-col items-start gap-4 px-4 py-6">
        <h1 className="text-2xl font-semibold text-foreground">{pageTitle}</h1>
        {scopedClient ? (
          <p className="text-sm text-muted-foreground">
            This page only accepts devices for {scopedClient.clientId}. To upload devices for
            multiple clients, use the{" "}
            <Link
              to="/alfred/devices/bulk-add"
              className="font-medium text-foreground underline underline-offset-4"
            >
              general bulk add page
            </Link>
            .
          </p>
        ) : null}
        <p className="text-sm text-muted-foreground">
          Upload a CSV to validate devices before starting the import.
        </p>
        <Stepper
          steps={BULK_IMPORT_STEPS}
          currentStep={
            currentStep === "import" && uploadResult
              ? BULK_IMPORT_STEPS.length
              : BULK_IMPORT_STEPS.findIndex((step) => step.id === currentStep)
          }
          className="w-full max-w-3xl py-2"
        />
        {!clients && !clientsError ? (
          <p role="status" className="text-sm text-muted-foreground">
            Loading clients…
          </p>
        ) : clientsError ? (
          <Alert variant="destructive" className="max-w-3xl">
            <AlertTitle>Unable to load clients</AlertTitle>
            <AlertDescription>
              Refresh the page before validating a bulk import file.
            </AlertDescription>
          </Alert>
        ) : isClientScoped && !scopedClient ? (
          <Alert variant="destructive" className="max-w-3xl">
            <AlertTitle>Unable to load client</AlertTitle>
            <AlertDescription>
              Return to the client device page and try opening Bulk add again.
            </AlertDescription>
          </Alert>
        ) : (
          <>
            {currentStep === "upload" ? (
              <AlfredBulkUploadStep
                requiredClientId={scopedClient?.clientId}
                validClientIds={validClientIds}
                selection={selection}
                onSelectionChange={setSelection}
                onContinue={() => setCurrentStep("preview")}
              />
            ) : null}
            {currentStep === "preview" && selection ? (
              <AlfredBulkPreviewStep
                selection={selection}
                onBack={() => setCurrentStep("upload")}
                onConfirm={handleConfirmImport}
              />
            ) : null}
            {currentStep === "import" && selection ? (
              <AlfredBulkImportStep
                fileName={selection.file.name}
                isSubmitting={isSubmitting}
                result={uploadResult}
                errorMessage={uploadError}
                onBack={() => {
                  setUploadError(null)
                  setCurrentStep("preview")
                }}
                onRetry={() => void submitImport()}
              />
            ) : null}
          </>
        )}
      </main>
    </>
  )
}
